import { useEffect, useState } from "react";

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  path: string;
}

export default function ShareModal({ open, onClose, title, path }: ShareModalProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  if (!open) return null;

  const fullUrl = `${window.location.origin}${__BASE_PATH__.replace(/\/$/, "")}${path}`;
  const encodedUrl = encodeURIComponent(fullUrl);
  const encodedTitle = encodeURIComponent(title);

  const platforms = [
    {
      key: "weibo",
      label: "微博",
      icon: "ri-weibo-fill",
      color: "bg-accent-100 text-accent-700",
      href: `https://service.weibo.com/share/share.php?url=${encodedUrl}&title=${encodedTitle}`
    },
    {
      key: "qzone",
      label: "QQ空间",
      icon: "ri-qq-fill",
      color: "bg-secondary-100 text-secondary-700",
      href: `https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=${encodedUrl}&title=${encodedTitle}`
    },
    {
      key: "x",
      label: "X",
      icon: "ri-twitter-x-fill",
      color: "bg-background-200 text-foreground-800",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`
    },
    {
      key: "telegram",
      label: "Telegram",
      icon: "ri-telegram-fill",
      color: "bg-primary-100 text-primary-700",
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`
    }
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
    } catch {
      const input = document.createElement("input");
      input.value = fullUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-6 animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-heading text-lg text-foreground-950">分享到</h3>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 cursor-pointer"
            aria-label="关闭"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="grid grid-cols-4 gap-3">
          {platforms.map((p) => (
            <a
              key={p.key}
              href={p.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-2 text-xs text-foreground-700"
            >
              <span className={`flex h-12 w-12 items-center justify-center rounded-lg ${p.color} transition-transform hover:scale-105`}>
                <i className={`${p.icon} text-2xl`}></i>
              </span>
              {p.label}
            </a>
          ))}
        </div>

        <button
          onClick={handleCopy}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-md border border-background-300 bg-background-100 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-200 cursor-pointer"
        >
          <i className={copied ? "ri-check-line text-primary-600" : "ri-link"}></i>
          {copied ? "链接已复制" : "复制链接"}
        </button>
      </div>
    </div>
  );
}